import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useAuthStore } from './auth'
import { STATUS_CODES } from '@/services/api/base'
import api from '@/services/api/customers'
import apiCustomer from '@/services/api/customers'
import Honeybadger from '@honeybadger-io/js'
import { getError } from '@/utils/errors'

export const useCustomerStore = defineStore('customer', () => {
  const id = ref('')
  const email = ref('')
  const name = ref('')
  const phone = ref('')
  const pendingEmail = ref('')
  const mfaRequired = ref(false)
  const loaded = ref(false)
  const authStore = useAuthStore()

  async function loadFromSessionToken() {
    if (loaded.value) return

    if (authStore.getToken()) {
      const response = await api.getCustomerDetails()

      if (!response.error) {
        id.value = response.id
        email.value = response.email
        name.value = response.name
        phone.value = response.phone || ''
        pendingEmail.value = response.pending_email || ''
        mfaRequired.value = response.mfa_required || false
        loaded.value = true
      } else {
        if (response.status_code == STATUS_CODES.UNAUTHORIZED) {
          authStore.clear()
          loaded.value = false
        } else {
          Honeybadger.notify('Error fetching customer details', {
            context: { response: response },
            tags: 'customer'
          })
        }
      }
    }
  }

  async function getPrimaryCardDetails(): Promise<PaymentCard | undefined> {
    const result = await apiCustomer.getPrimaryCardDetails()

    if (result && !result.error) {
      return {
        id: result.id,
        last4: result.last_four,
        expMonth: result.exp_month,
        expYear: result.exp_year,
        network: result.network,
        funding: result.funding,
        primary: result.primary,
        insertedAt: result.inserted_at,
        status: result.status
      }
    }
  }

  async function requestEmailChange(newEmail: string): Promise<ApiNoContentResponse> {
    return await apiCustomer.requestEmailChange(newEmail)
  }

  async function resendChangeEmailInstructions(): Promise<ApiNoContentResponse> {
    return await apiCustomer.resendChangeEmailInstructions()
  }

  async function updateDetails(details: CustomerDetailsRequest): Promise<ApiNoContentResponse> {
    const result = await apiCustomer.updateDetails(details)

    if (result.error) {
      return { error: getError(result) }
    }

    email.value = details['email'] ?? email.value
    name.value = details['name'] ?? name.value
    phone.value = details['phone'] ?? phone.value
    pendingEmail.value = details['pending_email'] ?? pendingEmail.value
    mfaRequired.value = details['mfa_required'] ?? mfaRequired.value

    return {}
  }

  async function verifyEmailChange(token: string): Promise<VerifyEmailChangeResponse> {
    return await apiCustomer.verifyEmailChange(token)
  }

  function clear() {
    id.value = ''
    email.value = ''
    name.value = ''
    phone.value = ''
    pendingEmail.value = ''
    loaded.value = false
    mfaRequired.value = false
  }

  return {
    id,
    email,
    name,
    phone,
    clear,
    loaded,
    pendingEmail,
    mfaRequired,
    loadFromSessionToken,
    getPrimaryCardDetails,
    requestEmailChange,
    resendChangeEmailInstructions,
    updateDetails,
    verifyEmailChange
  }
})
